<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.expiry_return.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <!-- ส่วนบน: เลือก Lot (spec 2026-11-03 §5) -->
      <card class="no-border-card" body-classes="px-0 pb-1">
        <template slot="header">
          <h3 class="mb-3">{{ $t("modern_trade.expiry_return.candidates_title") }}</h3>
          <div class="row align-items-end">
            <div class="col-md-3">
              <label class="form-control-label">{{ $t("modern_trade.company.name") }}</label>
              <el-select
                v-model="companyFilter"
                :placeholder="$t('all') || 'All'"
                clearable
                filterable
                @change="loadCandidates"
                class="w-100"
              >
                <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id"></el-option>
              </el-select>
            </div>
            <div class="col-md-2">
              <label class="form-control-label">{{ $t("modern_trade.expiry_return.days_within") }}</label>
              <el-select v-model="days" @change="loadCandidates" class="w-100">
                <el-option
                  v-for="opt in daysOptions"
                  :key="opt"
                  :label="$t('modern_trade.expiry_return.days_option', { days: opt })"
                  :value="opt"
                ></el-option>
              </el-select>
            </div>
            <div class="col-md-2">
              <base-button size="sm" type="primary" @click="loadCandidates">
                <i class="fa fa-search"></i>
              </base-button>
            </div>
          </div>
        </template>

        <div v-loading="loadingCandidates">
          <el-table
            :data="candidates"
            :row-key="candidateKey"
            :empty-text="$t('modern_trade.expiry_return.no_candidates')"
            header-row-class-name="thead-light"
          >
            <el-table-column :label="$t('modern_trade.company.name')" min-width="150">
              <template slot-scope="{ row }">{{ row.company_name || row.company_id }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.expiry_return.product')" min-width="240">
              <template slot-scope="{ row }">
                {{ productLabel(row).text || row.product_id }}
                <el-tag v-if="productLabel(row).noBarcode" size="mini" type="info" class="ml-1">
                  {{ $t("modern_trade.common.no_barcode") }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.stock.lot_no')" prop="lot_no" min-width="120"></el-table-column>
            <el-table-column :label="$t('modern_trade.stock.expired_date')" prop="expired_date" min-width="110"></el-table-column>
            <el-table-column :label="$t('modern_trade.expiry_return.days_left')" min-width="110" align="center">
              <template slot-scope="{ row }">
                <span :class="daysClass(row.days_left)">
                  {{ row.days_left !== undefined && row.days_left !== null ? row.days_left : "-" }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.expiry_return.available')" prop="qty" min-width="90" align="right"></el-table-column>
            <el-table-column v-if="isEditModernTrade" :label="$t('modern_trade.expiry_return.action')" min-width="110" align="center">
              <template slot-scope="{ row }">
                <base-button size="sm" type="warning" :disabled="!(row.qty > 0)" @click="openReturn(row)">
                  {{ $t("modern_trade.expiry_return.return_btn") }}
                </base-button>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </card>

      <!-- ส่วนล่าง: ประวัติการรับคืน (ไม่มีปุ่มยกเลิก — บันทึกผิดให้ใช้การปรับ Stock) -->
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <h3 class="mb-3">{{ $t("modern_trade.expiry_return.history_title") }}</h3>
          <div class="row align-items-end">
            <div class="col-md-3">
              <label class="form-control-label">{{ $t("modern_trade.expiry_return.date_from") }}</label>
              <el-date-picker
                v-model="historyFrom"
                type="date"
                value-format="yyyy-MM-dd"
                class="w-100"
                @change="reloadHistory"
              ></el-date-picker>
            </div>
            <div class="col-md-3">
              <label class="form-control-label">{{ $t("modern_trade.expiry_return.date_to") }}</label>
              <el-date-picker
                v-model="historyTo"
                type="date"
                value-format="yyyy-MM-dd"
                class="w-100"
                @change="reloadHistory"
              ></el-date-picker>
            </div>
            <div class="col-md-3">
              <label class="form-control-label">{{ $t("modern_trade.company.name") }}</label>
              <el-select
                v-model="historyCompany"
                :placeholder="$t('all') || 'All'"
                clearable
                filterable
                @change="reloadHistory"
                class="w-100"
              >
                <el-option v-for="c in companies" :key="c.id" :label="c.name" :value="c.id"></el-option>
              </el-select>
            </div>
            <div class="col-md-2">
              <base-button size="sm" type="primary" @click="reloadHistory">
                <i class="fa fa-search"></i>
              </base-button>
            </div>
          </div>
        </template>

        <div v-loading="loadingHistory">
          <el-table
            :data="history"
            row-key="id"
            :empty-text="$t('modern_trade.expiry_return.no_history')"
            header-row-class-name="thead-light"
          >
            <el-table-column :label="$t('modern_trade.expiry_return.return_date_col')" prop="return_date" min-width="110"></el-table-column>
            <el-table-column :label="$t('modern_trade.company.name')" min-width="150">
              <template slot-scope="{ row }">{{ row.company_name || row.company_id }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.expiry_return.product')" min-width="240">
              <template slot-scope="{ row }">
                {{ productLabel(row).text || row.product_id }}
                <el-tag v-if="productLabel(row).noBarcode" size="mini" type="info" class="ml-1">
                  {{ $t("modern_trade.common.no_barcode") }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.stock.lot_no')" prop="lot_no" min-width="120"></el-table-column>
            <el-table-column :label="$t('modern_trade.stock.expired_date')" prop="expired_date" min-width="110"></el-table-column>
            <el-table-column :label="$t('modern_trade.stock.qty')" prop="qty" min-width="80" align="right"></el-table-column>
            <el-table-column :label="$t('modern_trade.expiry_return.note')" prop="note" min-width="160"></el-table-column>
            <el-table-column :label="$t('modern_trade.expiry_return.recorded_by')" prop="created_by_name" min-width="130"></el-table-column>
          </el-table>
        </div>

        <div slot="footer" class="d-flex align-items-center justify-content-between">
          <p class="text-muted mb-0">{{ $t("total") }}: {{ historyTotal }}</p>
          <base-pagination
            v-model="historyPage"
            :per-page="historyPerPage"
            :total="historyTotal"
            @input="loadHistory"
          ></base-pagination>
        </div>
      </card>
    </div>

    <!-- dialog รับคืนทีละ Lot -->
    <el-dialog
      :title="$t('modern_trade.expiry_return.dialog_title')"
      :visible.sync="showDialog"
      width="480px"
      :close-on-click-modal="false"
    >
      <div v-if="target">
        <p class="mb-3">
          <strong>{{ productLabel(target).text || target.product_id }}</strong>
          <el-tag v-if="productLabel(target).noBarcode" size="mini" type="info" class="ml-1">
            {{ $t("modern_trade.common.no_barcode") }}
          </el-tag>
          <span class="d-block text-muted">
            {{ target.company_name || target.company_id }} · {{ $t("modern_trade.stock.lot_no") }} {{ target.lot_no }}
            · {{ $t("modern_trade.stock.expired_date") }} {{ target.expired_date }}
          </span>
        </p>
        <div class="form-group">
          <label class="form-control-label">{{ $t("modern_trade.expiry_return.qty") }} *</label>
          <input
            v-model="form.qty"
            class="form-control"
            type="number"
            min="1"
            step="1"
            :max="target.qty"
          />
          <small v-if="qtyErrorText" class="text-danger">{{ qtyErrorText }}</small>
          <small v-else class="text-muted">{{ $t("modern_trade.expiry_return.qty_hint", { max: target.qty }) }}</small>
        </div>
        <div class="form-group">
          <label class="form-control-label">{{ $t("modern_trade.expiry_return.return_date") }} *</label>
          <el-date-picker v-model="form.return_date" type="date" value-format="yyyy-MM-dd" class="w-100"></el-date-picker>
        </div>
        <div class="form-group mb-0">
          <label class="form-control-label">{{ $t("modern_trade.expiry_return.note") }}</label>
          <textarea v-model="form.note" class="form-control" rows="2"></textarea>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="showDialog = false">{{ $t("modern_trade.expiry_return.cancel") }}</el-button>
        <el-button type="warning" :loading="saving" :disabled="!canSubmit" @click="submitReturn">
          {{ $t("modern_trade.expiry_return.confirm") }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, DatePicker, Dialog, Button, Tag } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";
import { mtProductLabel } from "@/util/mt/productLabel";
import {
  localToday,
  returnQtyError,
  availableFromConflict,
  candidatesFromBody,
  historyFromBody,
  returnPayload,
} from "@/util/mt/expiryReturn";

export default {
  name: "MtExpiryReturns",
  components: {
    RouteBreadCrumb,
    BasePagination,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    [Tag.name]: Tag,
  },
  data() {
    return {
      companies: [],
      // ส่วนบน — ชุดวันเดียวกับรายงานหมดอายุ (MtExpiryReport): 450 / 365 / 180, ค่าเริ่มต้น 450
      companyFilter: null,
      days: 450,
      daysOptions: [450, 365, 180],
      candidates: [],
      loadingCandidates: false,
      // ส่วนล่าง — ประวัติ
      historyFrom: "",
      historyTo: "",
      historyCompany: null,
      history: [],
      historyTotal: 0,
      historyPage: 1,
      historyPerPage: 30,
      loadingHistory: false,
      // dialog
      showDialog: false,
      target: null,
      form: { qty: "", return_date: "", note: "" },
      saving: false,
    };
  },
  computed: {
    ...mapGetters({
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
    qtyError() {
      if (!this.target) return null;
      return returnQtyError(this.form.qty, this.target.qty);
    },
    qtyErrorText() {
      if (!this.qtyError) return "";
      return this.$t(`modern_trade.expiry_return.err_${this.qtyError}`, { max: this.target.qty });
    },
    canSubmit() {
      return !!this.target && !this.qtyError && !!this.form.return_date && !this.saving;
    },
  },
  mounted() {
    // ช่วงวันที่ประวัติเริ่มต้น = ต้นเดือนนี้ ถึง วันนี้ (วันตามเวลาเครื่อง ไม่ใช่ UTC)
    const today = localToday();
    this.historyFrom = today.slice(0, 8) + "01";
    this.historyTo = today;
    this.loadCompanies();
    this.loadCandidates();
    this.loadHistory();
  },
  methods: {
    // rows carry barcode / name / code (wms man_pn) — code is the no-barcode fallback
    productLabel(row) {
      return mtProductLabel({ barcode: row && row.barcode, name: row && row.name, code: row && row.code });
    },
    candidateKey(row) {
      return `${row.company_id}|${row.product_id}|${row.lot_no}`;
    },
    errMsg(err) {
      const d = (err && err.response && err.response.data) || {};
      return d.Cause || d.Msg || (err && err.message) || "Error";
    },
    async loadCompanies() {
      try {
        const res = await ModernTradeService.companiesList({ per_page: 200, status: 1 });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.companies = d.items || [];
      } catch (e) {
        // non-fatal
      }
    },
    async loadCandidates() {
      this.loadingCandidates = true;
      try {
        const params = { days: this.days };
        if (this.companyFilter) params.company_id = this.companyFilter;
        const res = await ModernTradeService.expiryReturnCandidates(params);
        this.candidates = candidatesFromBody(res.data);
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.loadingCandidates = false;
      }
    },
    reloadHistory() {
      this.historyPage = 1;
      this.loadHistory();
    },
    async loadHistory() {
      if (this.historyFrom && this.historyTo && this.historyFrom > this.historyTo) {
        swal.fire({ title: this.$t("modern_trade.expiry_return.err_history_range"), icon: "warning" });
        return;
      }
      this.loadingHistory = true;
      try {
        const params = { page: this.historyPage, per_page: this.historyPerPage };
        if (this.historyFrom) params.from = this.historyFrom;
        if (this.historyTo) params.to = this.historyTo;
        if (this.historyCompany) params.company_id = this.historyCompany;
        const res = await ModernTradeService.expiryReturnsList(params);
        const { items, total } = historyFromBody(res.data);
        this.history = items;
        this.historyTotal = total;
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.loadingHistory = false;
      }
    },
    openReturn(row) {
      this.target = Object.assign({}, row);
      // วันนี้คำนวณตอนเปิด dialog ทุกครั้ง (ไม่ cache) — หน้าเปิดค้างข้ามเที่ยงคืนจะได้ไม่ใช้วันเมื่อวาน
      this.form = { qty: String(row.qty), return_date: localToday(), note: "" };
      this.showDialog = true;
    },
    async submitReturn() {
      if (!this.canSubmit) return;
      const target = this.target;
      const payload = returnPayload(target, this.form);
      const result = await swal.fire({
        title: this.$t("modern_trade.expiry_return.confirm_title", { qty: payload.qty, lot: target.lot_no }),
        text: this.$t("modern_trade.expiry_return.confirm_text"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("modern_trade.expiry_return.confirm"),
        cancelButtonText: this.$t("modern_trade.expiry_return.cancel"),
      });
      if (!result.value) return;
      this.saving = true;
      try {
        await ModernTradeService.expiryReturnCreate(payload);
        this.showDialog = false;
        swal.fire({ title: this.$t("modern_trade.expiry_return.saved"), icon: "success", timer: 1500, showConfirmButton: false });
        this.loadCandidates();
        this.reloadHistory();
      } catch (err) {
        const status = err && err.response && err.response.status;
        const code = (err && err.response && err.response.data && err.response.data.Msg) || "";
        const available = status === 409 ? availableFromConflict(err.response.data) : null;
        if (available === 0) {
          // Lot หมดแล้ว — ไม่มีอะไรให้กรอกใหม่ ปิด dialog แล้วเอาแถวออก
          this.showDialog = false;
          this.candidates = this.candidates.filter(r => this.candidateKey(r) !== this.candidateKey(target));
          swal.fire({ title: this.$t("modern_trade.expiry_return.lot_empty"), icon: "warning" });
        } else if (available !== null) {
          // อีกคน/การขายตัด Lot นี้ไปก่อน — sync คงเหลือจริงเข้า dialog แล้วให้กรอกใหม่
          this.target = Object.assign({}, target, { qty: available });
          this.candidates = this.candidates.map(r =>
            this.candidateKey(r) === this.candidateKey(target) ? Object.assign({}, r, { qty: available }) : r
          );
          swal.fire({ title: this.$t("modern_trade.expiry_return.insufficient_stock", { available }), icon: "error" });
        } else if (code === "future_date") {
          swal.fire({ title: this.$t("modern_trade.expiry_return.err_future_date"), icon: "warning" });
        } else if (code === "sale_order_closed") {
          // Cause ของ BE เป็นข้อความไทยที่ระบุเลขใบสั่งขายที่ปิดแล้ว — แสดงตามนั้น
          const cause = err.response.data.Cause;
          swal.fire({ title: cause || this.$t("modern_trade.invoice.err_sale_order_closed"), icon: "warning" });
        } else {
          swal.fire({ title: this.errMsg(err), icon: "error" });
        }
      } finally {
        this.saving = false;
      }
    },
    daysClass(days) {
      if (days === undefined || days === null) return "";
      if (days < 0) return "text-danger font-weight-bold";
      if (days <= 30) return "text-warning font-weight-bold";
      return "";
    },
  },
};
</script>
